import React from "react";
import { Box, Typography } from "@mui/material";
import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import type { SortDirection } from "../../hooks/useListState";

export interface SortableColumnHeaderProps {
    /** Column label to display */
    label: string;
    /** Field name used for sorting */
    field: string;
    /** Whether this column is currently sorted */
    isSorted: boolean;
    /** Current sort direction (only shown if isSorted is true) */
    sortDirection?: SortDirection;
    /** Callback when header is clicked */
    onSort: (field: string) => void;
}

/**
 * Sortable column header component that displays a label with sort direction indicator.
 * Click to toggle sort direction or switch sort field.
 */
const SortableColumnHeader: React.FC<SortableColumnHeaderProps> = ({
    label,
    field,
    isSorted,
    sortDirection,
    onSort,
}) => {
    const handleClick = () => {
        onSort(field);
    };

    return (
        <Box
            onClick={handleClick}
            sx={{
                display: "inline-flex",
                alignItems: "center",
                cursor: "pointer",
                userSelect: "none",
                "&:hover": {
                    opacity: 0.8,
                },
            }}
        >
            <Typography
                variant="subtitle2"
                component="span"
                sx={{
                    fontWeight: isSorted ? 600 : 500,
                    color: isSorted ? "primary.main" : "text.secondary",
                }}
            >
                {label}
            </Typography>
            {isSorted && (
                <Box component="span" sx={{ ml: 0.5, display: "flex", alignItems: "center" }}>
                    {sortDirection === "asc" ? (
                        <ArrowUpward sx={{ fontSize: 16, color: "primary.main" }} />
                    ) : (
                        <ArrowDownward sx={{ fontSize: 16, color: "primary.main" }} />
                    )}
                </Box>
            )}
        </Box>
    );
};

export default SortableColumnHeader;

